import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import CrownIcon from "@/components/Crown";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { NAV_LINKS, WHATSAPP_DEFAULT, scrollToId } from "@/lib/site";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (open) {
            window.__lenis?.stop();
            document.body.style.overflow = "hidden";
        } else {
            window.__lenis?.start();
            document.body.style.overflow = "";
        }
    }, [open]);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => scrollToId(href), open ? 150 : 0);
    };

    return (
        <header
            data-testid="navbar-container"
            className={`sticky top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "glass border-b border-white/5 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <nav className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
                <a
                    href="#inicio"
                    data-testid="navbar-logo"
                    onClick={(e) => go(e, "#inicio")}
                    className="flex items-center gap-3 group"
                >
                    <span className="text-gold group-hover:text-gold-hover transition-colors">
                        <CrownIcon size={26} />
                    </span>
                    <span className="leading-none">
                        <span className="block font-display font-bold text-lg tracking-[0.18em] text-white">
                            CORTE <span className="text-gold">NOBRE</span>
                        </span>
                        <span className="block text-[9px] uppercase tracking-[0.4em] text-cream/50 mt-1">
                            Barbearia de Elite
                        </span>
                    </span>
                </a>

                <div className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={(e) => go(e, l.href)}
                            className="relative text-[13px] uppercase tracking-[0.18em] text-cream/70 hover:text-gold transition-colors duration-300 after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href={WHATSAPP_DEFAULT}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="navbar-agendar-cta"
                        className="hidden sm:inline-flex items-center gap-2 bg-gold-gradient text-obsidian text-[13px] font-semibold uppercase tracking-[0.12em] px-6 py-3 rounded-full transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_28px_rgba(212,175,55,0.45)]"
                    >
                        <WhatsAppIcon size={16} />
                        Agendar Horário
                    </a>
                    <button
                        data-testid="mobile-menu-toggle"
                        aria-label="Abrir menu"
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-gold p-2 rounded-full border border-gold/30"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden glass border-t border-white/5 overflow-hidden"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {NAV_LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    onClick={(e) => go(e, l.href)}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i }}
                                    className="py-3 font-display text-lg uppercase tracking-[0.2em] text-cream/80 hover:text-gold border-b border-white/5 last:border-0"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                            <a
                                href={WHATSAPP_DEFAULT}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-4 inline-flex items-center justify-center gap-2 bg-gold-gradient text-obsidian font-semibold uppercase tracking-[0.12em] px-6 py-4 rounded-full"
                            >
                                <WhatsAppIcon size={18} />
                                Agendar Horário
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
